import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createScheduledPayment,
  deleteScheduledPayment,
  getCashflowProjection,
  listScheduledPayments,
  updateScheduledPayment,
  type CashflowHorizon,
  type ScheduledPaymentFilters,
  type ScheduledPaymentInput,
} from '../lib/cashflowApi';

// Cash-flow data is not real-time; mirror useFinancials' 5-minute window.
const FIVE_MINUTES = 5 * 60 * 1000;

export function useCashflowProjection(months: CashflowHorizon, companyIds?: string[]) {
  return useQuery({
    queryKey: ['financials', 'cashflow', months, companyIds ?? []],
    queryFn: () => getCashflowProjection(months, companyIds),
    staleTime: FIVE_MINUTES,
  });
}

export function useScheduledPayments(filters: ScheduledPaymentFilters = {}) {
  return useQuery({
    queryKey: ['scheduled-payments', filters],
    queryFn: () => listScheduledPayments(filters),
    staleTime: FIVE_MINUTES,
  });
}

/** Invalidate both the scheduled-payments lists and the projection. */
function useInvalidateCashflow() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['scheduled-payments'] });
    void queryClient.invalidateQueries({ queryKey: ['financials', 'cashflow'] });
  };
}

export function useCreateScheduledPayment() {
  const invalidate = useInvalidateCashflow();
  return useMutation({
    mutationFn: (input: ScheduledPaymentInput) => createScheduledPayment(input),
    onSuccess: invalidate,
  });
}

export function useUpdateScheduledPayment() {
  const invalidate = useInvalidateCashflow();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ScheduledPaymentInput> }) =>
      updateScheduledPayment(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteScheduledPayment() {
  const invalidate = useInvalidateCashflow();
  return useMutation({
    mutationFn: (id: string) => deleteScheduledPayment(id),
    onSuccess: invalidate,
  });
}
